/**
 * Valtio state store for ViziMix Organize
 *
 * Server data is managed by React Query.
 * This store manages:
 * - Authentication state
 * - UI state (sidebar)
 */

import { proxy } from "valtio";

// =============================================================================
// Types
// =============================================================================

export interface User {
  id: string;
  email: string;
  displayName: string | null;
  creditBalance: number;
}

export type EventType =
  | "sports"
  | "concert"
  | "festival"
  | "conference"
  | "party"
  | "wedding"
  | "general";

export interface OutputTarget {
  style: "tiktok" | "instagram" | "youtube_short";
  count: number;
}

export interface MixEvent {
  id: string;
  title: string;
  description: string | null;
  slug: string;
  status: string;
  eventType: EventType;
  startsAt: number;
  endsAt: number;
  geoLatitude: number | null;
  geoLongitude: number | null;
  geoRadiusMeters: number;
  geoRequired: boolean;
  maxParticipants: number;
  maxRecordingSeconds: number;
  maxFileSizeMb: number;
  outputTargets: OutputTarget[] | null;
  recordingPrompt: string | null;
  estimatedCredits: number;
  actualCredits: number;
  joinCode: string | null;
  createdAt: number;
  updatedAt: number;
  // Computed from joins
  participantCount?: number;
  uploadCount?: number;
  approvedCount?: number;
}

export interface MixParticipant {
  id: string;
  displayName: string | null;
  email: string | null;
  geoVerified: boolean;
  recordingStatus: string;
  recordingDurationSeconds: number | null;
  joinedAt: number;
}

export interface MixUpload {
  id: string;
  participantId: string;
  participantName: string | null;
  fileId: string;
  moderationStatus: string;
  moderationNote: string | null;
  moderatedAt: number | null;
  durationSeconds: number | null;
  fileSizeBytes: number | null;
  uploadedAt: number;
  width?: number | null;
  height?: number | null;
  thumbnailUrl?: string;
  videoUrl?: string;
  fileStatus?: string;
}

export interface MixProduction {
  id: string;
  jobId: string | null;
  style: string;
  sequenceIndex: number;
  customPrompt: string | null;
  status: string;
  creditsCharged: number;
  progress: number;
  thumbnailUrl: string | null;
  posterUrl: string | null;
  createdAt: number;
  completedAt: number | null;
}

export interface CreateEventInput {
  title: string;
  description?: string;
  eventType?: EventType;
  startsAt: number;
  endsAt: number;
  geoLatitude?: number;
  geoLongitude?: number;
  geoRadiusMeters?: number;
  geoRequired?: boolean;
  maxParticipants?: number;
  maxRecordingSeconds?: number;
  maxFileSizeMb?: number;
  outputTargets?: OutputTarget[];
  recordingPrompt?: string;
}

// =============================================================================
// Store (UI state only - server data managed by React Query)
// =============================================================================

export type Theme = "light" | "dark" | "system";

interface Settings {
  theme: Theme;
}

const SETTINGS_KEY = "organize_settings";
const DEFAULT_SETTINGS: Settings = { theme: "dark" };

interface OrganizeState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;

  // Event-specific state
  events: MixEvent[];
  currentEvent: MixEvent | null;

  // UI
  sidebarCollapsed: boolean;
  settingsOpen: boolean;
  settings: Settings;
}

export const store = proxy<OrganizeState>({
  // Auth
  user: null,
  isAuthenticated: false,
  isAuthLoading: true,

  // Events
  events: [],
  currentEvent: null,

  // UI
  sidebarCollapsed: false,
  settingsOpen: false,
  settings: { ...DEFAULT_SETTINGS },
});

// =============================================================================
// Actions
// =============================================================================

export const actions = {
  // Auth
  setUser(user: User | null) {
    store.user = user;
    store.isAuthenticated = !!user;
    store.isAuthLoading = false;
  },

  setAuthLoading(loading: boolean) {
    store.isAuthLoading = loading;
  },

  logout() {
    store.user = null;
    store.isAuthenticated = false;
  },

  // Events
  setEvents(events: MixEvent[]) {
    store.events = events;
  },

  setCurrentEvent(event: MixEvent | null) {
    store.currentEvent = event;
  },

  // UI
  toggleSidebar() {
    store.sidebarCollapsed = !store.sidebarCollapsed;
  },

  setSidebarCollapsed(collapsed: boolean) {
    store.sidebarCollapsed = collapsed;
  },

  setSettingsOpen(open: boolean) {
    store.settingsOpen = open;
  },

  setTheme(theme: Theme) {
    store.settings.theme = theme;
    saveSettings();
  },
};

function saveSettings() {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(store.settings));
  } catch {
    // ignore
  }
}

export function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Settings>;
      if (parsed.theme && ["light", "dark", "system"].includes(parsed.theme)) {
        store.settings.theme = parsed.theme;
      }
    }
  } catch {
    // ignore
  }
}
